import { useEffect, useRef } from "react";
import { ArrowUpRight, ShieldCheck, MapPin, FileCheck2, AlertTriangle } from "lucide-react";
import Particles from "./Particles";
import Blueprint from "./Blueprint";

const SHIELD = "https://customer-assets.emergentagent.com/job_before-the-loss-1/artifacts/hyk3lwcd_514CC5D3-9081-44D7-8DD9-B78FADD7368F.png";

export const Hero = () => {
  const shieldRef = useRef(null);
  const panelsRef = useRef(null);

  // Mouse parallax (rAF-throttled, desktop only)
  useEffect(() => {
    if (window.matchMedia("(hover: none), (max-width: 768px)").matches) return;
    let raf = 0;
    let lastX = 0;
    let lastY = 0;
    const apply = () => {
      raf = 0;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const nx = (lastX - w / 2) / (w / 2);
      const ny = (lastY - h / 2) / (h / 2);
      if (shieldRef.current) {
        shieldRef.current.style.transform = `translate3d(${nx * 12}px, ${ny * 8}px, 0) rotate(${nx * 1.4}deg)`;
      }
      if (panelsRef.current) {
        panelsRef.current.style.transform = `translate3d(${nx * -8}px, ${ny * -5}px, 0)`;
      }
    };
    const onMove = (e) => {
      lastX = e.clientX;
      lastY = e.clientY;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-[100svh] flex items-center overflow-hidden pt-28 pb-20 btr-bg-glow"
      data-testid="hero-section"
    >
      {/* Animated grid background */}
      <div className="absolute inset-0 btr-grid-animated btr-grid-mask pointer-events-none" />

      {/* Red beams — drifting */}
      <div
        className="btr-beam btr-beam-drift"
        style={{ width: 720, height: 720, top: -240, right: -200, background: "#a2020b", opacity: 0.35 }}
      />
      <div
        className="btr-beam btr-beam-drift"
        style={{ width: 560, height: 560, bottom: -260, left: -180, background: "#2b2b2b", opacity: 0.55, animationDelay: "3s" }}
      />
      <div
        className="btr-beam btr-beam-drift"
        style={{ width: 380, height: 380, top: "38%", left: "12%", background: "#a2020b", opacity: 0.18, animationDelay: "6s" }}
      />

      {/* Sweeping light */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="btr-sweep" />
      </div>

      {/* Particles */}
      <Particles count={16} />

      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(0,0,0,0.78))]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center w-full">
        {/* Copy */}
        <div className="btr-reveal">
          <span className="btr-badge" data-testid="hero-badge">
            <span className="dot" />
            Pre-Loss Intelligence • Litigation-Ready Protection
          </span>

          <h1
            className="font-display font-extrabold text-white mt-6 text-[2.6rem] leading-[1] sm:text-6xl lg:text-7xl sm:leading-[0.95] tracking-tight"
            data-testid="hero-headline"
          >
            Before the Loss.<br />
            Before the <span className="text-[#a2020b]">Litigation</span>.<br />
            Before the Risk.
          </h1>

          <p className="font-body text-zinc-300/90 text-[15px] sm:text-lg mt-6 sm:mt-7 max-w-xl leading-relaxed" data-testid="hero-subheadline">
            Florida and Georgia&rsquo;s premier loss consulting and risk management firm for high-value properties,
            HOA communities, commercial owners, and attorneys who need documentation that holds up when everything is on the line.
          </p>

          <div className="flex flex-wrap gap-4 mt-9">
            <a href="#contact" className="btr-btn-primary btr-cta-breathe" data-testid="hero-cta-primary">
              <ShieldCheck className="w-4 h-4" />
              Schedule a Risk Consultation
            </a>
            <a href="#services" className="btr-btn-ghost" data-testid="hero-cta-secondary">
              Explore Our Services
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          <div className="flex items-center gap-2 mt-8 text-zinc-400 text-xs font-body tracking-wider uppercase">
            <MapPin className="w-3.5 h-3.5 text-[#a2020b]" />
            Serving Florida &amp; Georgia • Built for High-Stakes Property Protection
          </div>
        </div>

        {/* Right visual – layered glass panels + shield */}
        <div ref={panelsRef} className="btr-reveal relative flex justify-center lg:justify-end btr-parallax mt-4 lg:mt-0">
          <div className="relative w-[260px] xs:w-[300px] sm:w-[440px] lg:w-[540px] aspect-square">
            {/* Orbit rings */}
            <div className="absolute inset-0 rounded-full border border-white/8" />
            <div className="absolute inset-6 rounded-full border border-white/6" />
            <div className="absolute inset-14 rounded-full border border-[#a2020b]/30" />

            {/* Glow */}
            <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,rgba(162,2,11,0.45),transparent_60%)] blur-2xl" />
            <div className="absolute inset-10 rounded-full bg-[radial-gradient(circle_at_60%_40%,rgba(255,255,255,0.08),transparent_55%)] blur-2xl" />

            {/* Floating blueprint panel (back) */}
            <div className="absolute -left-6 top-10 w-40 sm:w-48 aspect-[3/4] btr-glass rounded-md p-3 rotate-[-7deg] hidden sm:block">
              <div className="text-[9px] font-display tracking-[0.25em] uppercase text-zinc-400 mb-2">Property Blueprint</div>
              <div className="h-[calc(100%-1.25rem)] w-full">
                <Blueprint showLabel={false} />
              </div>
            </div>

            {/* Floating report card (front-left) */}
            <div className="absolute -left-8 bottom-6 w-44 sm:w-52 btr-glass rounded-md p-4 rotate-[4deg] hidden sm:block">
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-display tracking-[0.25em] uppercase text-zinc-500">Pre-Loss Report</span>
                <FileCheck2 className="w-3.5 h-3.5 text-[#a2020b]" />
              </div>
              <div className="mt-3 space-y-1.5">
                {[90, 64, 82].map((w, i) => (
                  <div key={i} className="h-1 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#a2020b] to-[#a2020b]/30" style={{ width: `${w}%` }} />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[9px] font-body text-zinc-500">Risk Score</span>
                <span className="font-display font-bold text-white text-xs">84<span className="text-zinc-500">/100</span></span>
              </div>
            </div>

            {/* Exposure flag tag (top-right) */}
            <div className="absolute -right-2 top-4 btr-glass rounded-full px-3 py-1.5 flex items-center gap-1.5 btr-tag-pulse hidden sm:flex">
              <AlertTriangle className="w-3 h-3 text-[#a2020b]" />
              <span className="text-[10px] font-display font-bold tracking-[0.2em] uppercase text-white">Exposure Flagged</span>
            </div>

            {/* Shield centerpiece with parallax */}
            <div ref={shieldRef} className="absolute inset-0 flex items-center justify-center btr-parallax">
              <div className="btr-float relative">
                <img
                  src={SHIELD}
                  alt="Before The Risk shield emblem"
                  width="540"
                  height="540"
                  decoding="async"
                  className="w-[320px] sm:w-[380px] lg:w-[440px] object-contain drop-shadow-[0_0_55px_rgba(162,2,11,0.65)]"
                  data-testid="hero-shield"
                />
              </div>
            </div>

            {/* Orbit dots */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#a2020b] btr-red-glow" />
            <div className="absolute bottom-6 left-[18%] w-1 h-1 rounded-full bg-white/60" />
            <div className="absolute top-[30%] right-2 w-1 h-1 rounded-full bg-white/40" />

            {/* Bottom-right legal doc sliver */}
            <div className="absolute -right-4 bottom-10 w-32 sm:w-36 btr-glass rounded-md p-3 rotate-[6deg] hidden sm:block">
              <div className="text-[9px] font-display tracking-[0.25em] uppercase text-zinc-500">Legal File</div>
              <div className="mt-2 space-y-1">
                <div className="h-1 bg-white/10 rounded-full w-full" />
                <div className="h-1 bg-white/10 rounded-full w-[78%]" />
                <div className="h-1 bg-white/10 rounded-full w-[62%]" />
              </div>
              <div className="mt-2 text-[9px] font-display text-[#a2020b] tracking-[0.2em]">LITIGATION-READY</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom hairline */}
      <div className="absolute bottom-0 left-0 right-0 btr-hairline" />
    </section>
  );
};
